import { useQueries } from '@tanstack/react-query';
import { EntityAssociationType, SystemEntity } from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { useEntityAssociationsList } from '@/api/rpc/hooks/core/useEntityAssociations';
import {
  listAggregationTargets,
  listOrderLineTargets,
  salesOrderLines,
  transferOrderLines,
  type AggregationTargetOption,
  type OrderDocumentLines,
} from './aggregateLineItemsForm';
import {
  buildMatchedLineNumbers,
  selectRelatedDocuments,
} from './relatedLinesSummary';
import { useLineItemAssociationsMap } from './useLineItemAssociationsMap';
import { useRelatedLinesSummary } from './useRelatedLinesSummary';

interface UseAggregationTargetsInput {
  invoiceId: string;
  supplierId?: string | null;
  /** Every line of the invoice; matches are mapped to their line numbers. */
  allLineItems: { id: string; lineNumber: number }[];
  selectedLineNumbers: ReadonlySet<number>;
}

/**
 * Everything the selected lines can be aggregated onto: the related
 * documents' charges and PO lines (from the related-lines summary) plus the
 * product lines of related sales and transfer orders.
 */
export function useAggregationTargets({
  invoiceId,
  supplierId,
  allLineItems,
  selectedLineNumbers,
}: UseAggregationTargetsInput): {
  targets: AggregationTargetOption[];
  hasDocuments: boolean;
  isLoading: boolean;
} {
  const { summary, hasDocuments, isLoading } = useRelatedLinesSummary({
    invoiceId,
    lineItems: allLineItems,
    invoiceTotal: null,
    invoiceSupplierId: supplierId,
    showHistory: false,
  });

  const invoiceAssociations = useEntityAssociationsList({
    entityId: invoiceId,
    entityType: SystemEntity.AP_INVOICE,
    associationType: EntityAssociationType.AP_INVOICE_MATCHING,
  });
  const documents = selectRelatedDocuments(
    invoiceAssociations.data?.associations ?? [],
  );
  const salesOrders = documents.filter(
    (document) => document.entityType === SystemEntity.AR_SALES_ORDER,
  );
  const transferOrders = documents.filter(
    (document) => document.entityType === SystemEntity.IM_TRANSFER_ORDER,
  );

  const soLines = useQueries({
    queries: salesOrders.map((document) => ({
      ...orpc.accountsReceivable.salesOrders.get.queryOptions({
        input: { id: document.entityId },
      }),
      staleTime: 30_000,
    })),
    combine: (results) => ({
      lines: results.map((result) => result.data?.lineItems ?? []),
      isLoading: results.some((result) => result.isLoading),
    }),
  });
  const toLines = useQueries({
    queries: transferOrders.map((document) =>
      orpc.inventoryManagement.transferOrders.get.queryOptions({
        input: { id: document.entityId },
      }),
    ),
    combine: (results) => ({
      lines: results.map((result) => result.data?.lineItems ?? []),
      isLoading: results.some((result) => result.isLoading),
    }),
  });

  const lineAssociations = useLineItemAssociationsMap(allLineItems);
  const matches = buildMatchedLineNumbers(allLineItems, lineAssociations);

  const orderDocuments: OrderDocumentLines[] = [
    ...soLines.lines.map(salesOrderLines),
    ...toLines.lines.map(transferOrderLines),
  ];
  return {
    targets: [
      ...listAggregationTargets(summary.groups, selectedLineNumbers),
      ...listOrderLineTargets(orderDocuments, matches, selectedLineNumbers),
    ],
    hasDocuments,
    isLoading: isLoading || soLines.isLoading || toLines.isLoading,
  };
}
